import React, { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { withRouter, useLocation } from 'react-router-dom';

import AuthApi, {
  AuthenticateResponse,
  MfaChallengeType,
  SessionAuthenticateResponse,
  UserAuthDTO,
  UserOrganizationalUnitRoleAuthDTO,
} from '../api/AuthApi';
import {
  clearSession,
  endSession,
  getCurrentSession,
  onSessionEvent,
  publishSession,
  refreshSession,
  replaceAccessToken,
  SessionEndReason,
} from '../auth/session';
import { startActivityTracking } from '../auth/activity';
import { allTabsWereClosed, startTabPresence } from '../auth/tabPresence';
import { getAvatarColor } from '../util/avatarColor';
import { Trans } from '@lingui/react/macro';
import { useBootLoading } from './BootLoadingContext';
import RedirectLoader, { isRedirectPath } from '../components/RedirectLoader';
import RedirectLoaderScreen from '../components/RedirectLoaderScreen';
import useLoader from '../util/useLoader';
import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';

export const AuthContext = React.createContext<AuthContextValue>((null as unknown) as AuthContextValue);

type OrganizationalUnitRole = {
  id: number;
  userId: number;
  organizationalUnitId: number;
  organizationalUnit: OrganizationalUnit;
} & RoleWithFlags;
type Role = {
  name: string;
};
type RoleWithFlags = {
  roleId: number;
  role: Role;
  mainRole: boolean;
  canWorkAs: boolean;
  canBeAssigned: boolean;
  canAssign: boolean;
};
type OrganizationalUnit = {
  id: number;
};
type UserOrganizationalUnitEntry = OrganizationalUnit & {
  roles: RoleWithFlags[];
};

type AuthContextUser = UserAuthDTO & {
  actualOrganizationalUnitId?: number;
};

/** The admin impersonating the current user, as reported by the server. */
export type Impersonator = {
  id: number;
  name: string;
};

export class UserStoreUser {
  email: string;
  logOutUrl: string;
  user: AuthContextUser;
  impersonator: Impersonator | null;
  permissions: Record<string, 1>;
  organizationalUnitRoles: UserOrganizationalUnitRoleAuthDTO[];
  organizationalUnits: UserOrganizationalUnitEntry[];
  rolesByName: Record<number, Record<string, OrganizationalUnitRole>>;
  get name() {
    return this?.user;
  }

  constructor({ user, logOutUrl, impersonator }: { user: AuthContextUser; logOutUrl: string; impersonator?: Impersonator | null }) {
    this.email = user.email;
    this.logOutUrl = logOutUrl;
    this.user = user;
    this.impersonator = impersonator ?? null;
    this.permissions = (user.effectivePermissions || []).reduce((_, p) => {
      _[p] = 1;

      return _;
    }, {} as Record<string, 1>);
    this.organizationalUnitRoles = user.organizationalUnitRoles;
    this.organizationalUnits = (user.organizationalUnitRoles || []).reduce(
      (_, { id, userId, organizationalUnit, ...roleWithFlags }) => {
        if (!_.index[organizationalUnit.id]) {
          const entry = { ...organizationalUnit, roles: [] };
          _.list.push(entry);
          _.index[organizationalUnit.id] = entry;
        }

        _.index[organizationalUnit.id].roles.push(roleWithFlags);

        return _;
      },
      { list: [], index: {} } as {
        list: UserOrganizationalUnitEntry[];
        index: Record<number, UserOrganizationalUnitEntry>;
      }
    ).list;
    this.rolesByName = (user.organizationalUnitRoles || []).reduce((acc, our) => {
      const orgId = our.organizationalUnitId;
      const roleName = our.role.name;

      if (!acc[orgId]) {
        acc[orgId] = {};
      }

      acc[orgId][roleName] = our;

      return acc;
    }, {} as Record<number, Record<string, OrganizationalUnitRole>>);
  }

}

export type MFAInfo = {
  token: string;
  type?: MfaChallengeType;
};

/** localStorage key of the user left over from before cookie sessions (it held a 7-day token). */
const LEGACY_USER_KEY = 'user';
/**
 * The only auth-adjacent values still kept in localStorage, and why each is harmless there: the logout
 * URL (an integration's public page — the refresh response cannot supply it, so it has to survive a
 * reload) and the org unit the user chose to work in (a UI preference). Neither can authenticate anyone.
 * (../auth/tabPresence also keeps a timestamp of the last tab closing, likewise harmless.)
 */
const LOG_OUT_URL_KEY = 'auth:logOutUrl';
const orgUnitKey = (userId: number) => `auth:orgUnit:${userId}`;

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the preference just won't survive a reload.
  }
}

/**
 * In-memory holder of who is logged in. Rebuilt from the server on every page load — the access token
 * lives in ../auth/session, and nothing here is persisted except the two preferences above.
 */
export class UserStore {
  user: UserStoreUser | null | undefined = null;
  mfa: MFAInfo | null = null;
  /** Why the last session ended, when the server said; cleared by the next session. */
  endReason: SessionEndReason | null = null;

  static init() {
    // Before cookie sessions the user AND a 7-day token lived here. Remove it so the token stops
    // sitting on disk; this makes everyone log in once after the upgrade.
    writeStorage(LEGACY_USER_KEY, null);
    return new UserStore();
  }

  /** Builds the store's user from a session response, restoring the user's chosen org unit. */
  buildUserFrom(response: SessionAuthenticateResponse) {
    const user: AuthContextUser = response.user;
    const logOutUrl = response.logOutUrl ?? readStorage(LOG_OUT_URL_KEY);
    writeStorage(LOG_OUT_URL_KEY, logOutUrl);

    const savedOrgUnit = Number(readStorage(orgUnitKey(user.id)));
    const canWorkInSaved = (user.organizationalUnitRoles || []).some(r => r.organizationalUnitId === savedOrgUnit);
    user.actualOrganizationalUnitId = canWorkInSaved ? savedOrgUnit : user?.organizationalUnit?.id;

    return new UserStoreUser({
      user,
      logOutUrl: logOutUrl ?? '',
      impersonator: response.impersonatorUserId
        ? { id: response.impersonatorUserId, name: response.impersonatorName ?? '' }
        : null,
    });
  }

  setUser(newUser?: UserStoreUser | null) {
    this.user = newUser;
    if (!newUser) writeStorage(LOG_OUT_URL_KEY, null);

    // Cancel out any mfa token
    this.mfa = null;
  }

  setMfaInfo(token: string, type?: MfaChallengeType) {
    this.mfa = { token, type };
  }

  deleteMfaInfo() {
    this.mfa = null;
  }

  getMfaToken() {
    return this.mfa?.token;
  }
}

const USER_STORE = UserStore.init();

/** Whether `response` authenticates the same user, under the same impersonator, that `current` shows. */
export function isSameIdentity(current: UserStoreUser | null | undefined, response: SessionAuthenticateResponse) {
  return (
    !!current &&
    current.user.id === response.user.id &&
    (current.impersonator?.id ?? null) === (response.impersonatorUserId ?? null)
  );
}

function _AuthProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const [loading, , loadFn] = useLoader(true);
  const [updateCt, setUpdateCt] = useState(0);
  const location = useLocation();
  const { show, hide } = useBootLoading();

  const signalUpdate = () => setUpdateCt(Math.random() + new Date().getTime());
  const value = useMemo(() => new AuthContextValue(USER_STORE, signalUpdate), [USER_STORE, updateCt]);

  useEffect(() => {
    // Every session change — this tab's logins and refreshes, and other tabs' — lands here, so the
    // UI always shows whoever the cookie currently authenticates.
    const unsubscribe = onSessionEvent(event => {
      const previousUser = USER_STORE.user;
      USER_STORE.setUser(event.type === 'ended' ? null : USER_STORE.buildUserFrom(event.response));
      USER_STORE.endReason = event.type === 'ended' ? event.reason ?? null : null;
      // A renewed token for whoever this tab already shows keeps the store current but publishes no new
      // context value, which would reload every page that depends on it. The next update shows the store.
      if (event.type === 'session-refresh' && isSameIdentity(previousUser, event.response)) {
        return
      };
      signalUpdate();
    });
    // Coming back after closing every tab of the app ends the session instead of resuming it.
    loadFn(async () => ((await allTabsWereClosed()) ? endSession() : refreshSession()));
    // User input keeps the session from idling out; it reports nothing while logged out.
    const stopActivityTracking = startActivityTracking();
    const stopTabPresence = startTabPresence();
    return () => {
      unsubscribe();
      stopActivityTracking();
      stopTabPresence();
    };
  }, []);

  useLayoutEffect(() => {
    if (loading && !isRedirectPath(location?.pathname)) {
      show(<Trans>Verifying your session…</Trans>);
    } else {
      hide();
    }
  }, [loading, location?.pathname]);

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        isRedirectPath(location?.pathname) ? (
          <RedirectLoaderScreen>
            <RedirectLoader />
          </RedirectLoaderScreen>
        ) : null
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

// API helpers read the token from here on every request.
export { getToken } from '../auth/session';

export type SignalUpdateFn = () => void;
export type OTP = any;
type MSALInstance = IPublicClientApplication;

export class AuthContextValue {
  userStore: UserStore;
  signalUpdate: SignalUpdateFn;
  user: UserStoreUser | null | undefined;
  isLoggedIn: boolean;
  /** The last session was signed out by the server for inactivity. */
  loggedOutForInactivity: boolean;
  isPendingMfaVerification: boolean;
  mfaType: MfaChallengeType | undefined;
  avatarColor: string;

  constructor(userStore: UserStore, signalUpdate: SignalUpdateFn) {
    this.userStore = userStore;
    this.signalUpdate = signalUpdate;
    this.user = userStore.user;
    this.isLoggedIn = !!userStore.user;
    this.loggedOutForInactivity = !userStore.user && userStore.endReason === 'idle-timeout';
    this.isPendingMfaVerification = !!userStore.mfa;
    this.mfaType = userStore.mfa?.type;
    const u = userStore.user?.user;
    this.avatarColor = getAvatarColor(u ? `${u.firstName} ${u.lastName}` : '');
  }

  /** Makes a login/impersonation response the session in every tab; the provider updates the store. */
  private startSession(response: AuthenticateResponse | SessionAuthenticateResponse) {
    publishSession(response);
    this.signalUpdate();
  }

  async login({ email, password }: { email: string; password: string }) {
    const response = await AuthApi.login({ email, password });

    if (response.mfaVerificationRequired) {
      // The token is an interim MFA-challenge token, not a session; verifyMfa exchanges it.
      this.userStore.setMfaInfo(response.token, response.mfaVerificationType);
      this.signalUpdate();
    } else {
      this.startSession(response);
    }
  }

  async verifyMfa({ otp }: { otp: OTP }) {
    const response = await AuthApi.verifyMfa({ otp, token: this.userStore.getMfaToken() });
    this.startSession(response);
  }

  // Do we define a route to tell the backend to CANCEL the challenge? Or not?
  cancelMfaVerification() {
    this.userStore.deleteMfaInfo();
    this.signalUpdate();
  }

  async loginWithToken({ token }: { token: string }) {
    this.startSession(await AuthApi.loginWithToken({ token }));
  }

  async loginKerberosSso() {
    this.startSession(await AuthApi.loginKerberosSso(undefined));
  }

  async loginWithSSO({ ssoAccount, msalInstance }: { ssoAccount: AccountInfo; msalInstance: MSALInstance }) {
    if (!ssoAccount) return;

    const clientId = await AuthApi.getADClientId(undefined);
    if (!clientId) return;

    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/Usage`],
      account: ssoAccount,
    });

    const accessToken = tokenResponse?.accessToken;
    if (!accessToken) return;

    return this.login({
      email: `sso:${ssoAccount.username}`,
      password: `token:${accessToken}`,
    });
  }

  async logout({ msalInstance }: { msalInstance: MSALInstance }) {
    const logOutUrl = this.userStore?.user?.logOutUrl;
    const accounts = msalInstance?.getAllAccounts();

    // Revokes the session server-side and logs out every tab.
    await endSession();

    // Handle SSO logout
    if (accounts?.length > 0) {
      await msalInstance.logoutRedirect({
        account: accounts[0],
      });
    }
    // Fallback to backend logout URL
    if (logOutUrl) {
      document.location = logOutUrl;
    }
  }

  setOrganizationalUnit(orgUnitId: number) {
    const user = this.userStore?.user?.user;
    if (user) {
      user.actualOrganizationalUnitId = orgUnitId;
      writeStorage(orgUnitKey(user.id), String(orgUnitId));
      this.signalUpdate();
    }
  }

  setCompletedProfile(hasCompletedProfile: boolean) {
    if (this.userStore?.user?.user) {
      this.userStore.user.user.hasCompletedProfile = hasCompletedProfile;
    }
  }

  setChangePassword(changePassword: boolean) {
    if (this.userStore?.user?.user) {
      this.userStore.user.user.changePassword = changePassword;
    }
  }

  /**
   * Switches this browser (every tab) to a session for `userId`. The admin's own session is held by the
   * server until stopImpersonation — nothing of it stays in the browser.
   */
  async impersonate({ userId }: { userId: number }) {
    this.startSession(await AuthApi.impersonate({ userId }));
  }

  async stopImpersonation() {
    try {
      this.startSession(await AuthApi.stopImpersonation(undefined));
    } catch (e) {
      // Offline: keep impersonating rather than logging out; the user can try again.
      if (e instanceof TypeError) throw e;
      // The admin's own session ended while impersonating (logged out elsewhere, expired): the server
      // already cleared the cookie, so there is nothing to go back to but the login page.
      clearSession();
    }
  }

  /**
   * Re-fetches the current user's permissions/roles/org from the backend without a new login. Use this
   * after any change to the logged-in user's own permissions/roles/org (e.g. a profile edit) so the UI
   * reflects it immediately.
   */
  async refreshUser() {
    if (!this.userStore.user) return;
    this.startSession(await AuthApi.refresh(undefined));
  }

  /**
   * Swaps the current session's access token for a replacement issued by the backend — e.g. after a
   * password change, which revokes the token that made the request. No-op when `token` is empty.
   */
  replaceToken(token?: string | null) {
    if (token && getCurrentSession()) replaceAccessToken(token);
  }
}

export const AuthConsumer = AuthContext.Consumer;

export const AuthProvider = withRouter(_AuthProvider as any);
